import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import type { IDrillDownFilterContentProps } from './DrillDownFilter.types'
import DrillDownFilterContent from './DrillDownFilterContent'
import {
  DESKTOP_LAYOUT,
  IDrillDownFilterLayout,
  MOBILE_LAYOUT,
} from './DrillDownFilterLayout'
import { useDrillDownFilterShell } from './DrillDownFilterShellContext'

export type IDrillDownFilterPlatformProps<T = unknown> = Omit<
  IDrillDownFilterContentProps<T>,
  'density' | 'footerLayout' | 'autoFocusSearch' | 'showRootHeader' | 'onCancel'
> & {
  onCancel?: () => void
}

function PlatformFilter<T>({
  layout,
  onApply,
  onCancel,
  ...rest
}: IDrillDownFilterPlatformProps<T> & { layout: IDrillDownFilterLayout }) {
  const { close } = useDrillDownFilterShell()

  const handleApply = React.useCallback(
    (ids: string[]) => {
      onApply(ids)
      close()
    },
    [onApply, close],
  )

  const handleCancel = React.useCallback(() => {
    onCancel?.()
    close()
  }, [onCancel, close])

  return (
    <DrillDownFilterContent
      {...rest}
      {...layout}
      onApply={handleApply}
      onCancel={handleCancel}
    />
  )
}

export function DrillDownFilterDesktop<T>(
  props: IDrillDownFilterPlatformProps<T>,
) {
  return <PlatformFilter {...props} layout={DESKTOP_LAYOUT} />
}

export function DrillDownFilterMobile<T>(
  props: IDrillDownFilterPlatformProps<T>,
) {
  return <PlatformFilter {...props} layout={MOBILE_LAYOUT} />
}

// Runtime-resolved platform node: reads isMobileEnv at this single seam so
// cross-platform hosts pass content props once instead of branching Mobile vs
// Desktop themselves. Fixed-platform surfaces use .Mobile / .Desktop directly.
export function DrillDownFilterPlatform<T>(
  props: IDrillDownFilterPlatformProps<T>,
) {
  const { isMobileEnv } = React.useContext(MobXProviderContext) as {
    isMobileEnv?: boolean
  }

  return (
    <PlatformFilter
      {...props}
      layout={isMobileEnv ? MOBILE_LAYOUT : DESKTOP_LAYOUT}
    />
  )
}
